import { PlusOne } from "@mui/icons-material";
import CachedIcon from "@mui/icons-material/Cached";
import FilterListIcon from "@mui/icons-material/FilterList";
import PersonIcon from "@mui/icons-material/Person";
import SearchOff from "@mui/icons-material/SearchOff";
import {
  Box,
  Grid2,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useTranslationHook } from "../../hooks";
import { Role } from "../../types/interfaces/account";
import { PrivateSettings } from "../../types/interfaces/app";
import { UserResponse } from "../../types/model/user/UserResponse";
import { StoreInterfaces } from "../../types/store";

interface Props {
  tableData: UserResponse[];
  accountId?: number;
  onRequestRefresh: () => void;
  onRowClick: (user: UserResponse) => void;
  showAddButton?: boolean;
  disablePagination?: boolean;
}

const UserTable = ({
  tableData,
  accountId, 
  onRequestRefresh, 
  onRowClick,
  showAddButton,
  disablePagination = false,
}: Props ) => {
  const { t } = useTranslationHook();
  const navigate = useNavigate();

  const privateSettings: PrivateSettings | undefined = useSelector(
    (state: StoreInterfaces) => state.app.privateSettings,
    shallowEqual,
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<Record<string, any>>({});

  const handleRequestAdd = () => {
    const params = new URLSearchParams();
    if (accountId) params.set("accountId", accountId.toString());

    navigate(`/admin/users/add-user?${params.toString()}`);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const getUserIcon = (user: UserResponse, roles: Role[] | undefined) => {
    let color: string | undefined;
    let statusKey: string;

    const userRole = roles?.find((role) => role.id === user.roleId);
    const roleLabel = userRole?.i18nLabel || "generic.unknown-role";

    if (!user.active) {
      color = "error.main";
      statusKey = "generic.deleted";
    } else if (user.enabled) {
      statusKey = "generic.enabled";

      if (userRole) {
        if (userRole.type === "ADMIN") {
          color = "primary.dark";
        } else {
          color = "primary.main";
        }
      }
    } else {
      color = "text.secondary";
      statusKey = "generic.disabled";
    }

    return (
      <Tooltip title={`${t(roleLabel)} – ${t(statusKey)}`} arrow>
        <PersonIcon sx={{ color }} />
      </Tooltip>
    );
  };

  const filteredData = tableData.filter((user) => {
    let matches = true;
    for (const key in filters) {
      if (filters[key]) {
        const filterValue = String(filters[key]).toLowerCase();
        const userValue = String(user[key as keyof UserResponse]).toLowerCase();
        if (!userValue.includes(filterValue)) {
          matches = false;
          break;
        }
      }
    }
    return matches;
  });

  const visibleRows = disablePagination
    ? filteredData
    : filteredData.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      );
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredData.length) : 0;

  const hasData = visibleRows.length > 0;

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2, p: 2 }}>
        <Toolbar>
          <Typography sx={{ flex: "1 1 100%" }} variant="h4">
            {t("admin.users.table.title")}
          </Typography>

          {/* Filter button */}
          <Tooltip title={t("generic.filter")}>
            <IconButton onClick={() => setShowFilters(!showFilters)}>
              <FilterListIcon />
            </IconButton>
          </Tooltip>

          {/* Refresh button */}
          <Tooltip title={t("generic.refresh")}>
            <IconButton onClick={onRequestRefresh}>
              <CachedIcon />
            </IconButton>
          </Tooltip>

          {showAddButton && (
            <Tooltip title={t("admin.users.table.add-user")}>
              <IconButton onClick={handleRequestAdd}>
                <PlusOne />
              </IconButton>
            </Tooltip>
          )}
        </Toolbar>

        {showFilters && (
          <Grid2 container spacing={3} sx={{ mb: 2 }}>
            <Grid2 size={12}>
              <TextField
                name="name"
                label={t("generic.search")}
                value={filters.name ?? ""}
                onChange={handleFilterChange}
                fullWidth
              />
            </Grid2>
          </Grid2>
        )}

        <TableContainer>
          <Table sx={{ minWidth: 750 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t("admin.users.table.name")}</TableCell>
                <TableCell>{t("admin.users.table.email")}</TableCell>
                <TableCell>{t("admin.users.table.account")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {hasData ? (
                <>
                  {visibleRows.map((row) => (
                    <TableRow
                      hover
                      key={row.id}
                      onClick={() => onRowClick(row)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {getUserIcon(row, privateSettings?.roles)}
                          <Typography variant="body2">{row.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.accountName}</TableCell>
                    </TableRow>
                  ))}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={2} />
                    </TableRow>
                  )}
                </>
              ) : (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 10 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        opacity: 0.6,
                      }}
                    >
                      <SearchOff sx={{ fontSize: 64, mb: 2 }} />
                      <Typography variant="h6">
                        {t("admin.users.table.no-users")}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!disablePagination && (
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>
    </Box>
  );
};

export default UserTable;
